'use client';

import { useEffect, useState } from 'react';

interface HeroMediaProps {
  videoSrc: string;
  posterSrc: string;
}

/**
 * Hero background media with a mobile-first performance posture
 * (BAL-662; PageSpeed 2026-07-14: mobile LCP 3.1s, perf 60).
 *
 * The base layer is ALWAYS the poster <img> — it is the LCP node,
 * preloaded at high priority by the hero, and it is what no-JS,
 * crawlers, reduced-motion, Save-Data, and phones get. The ambient
 * video is a desktop-only progressive enhancement mounted after idle,
 * so a phone never downloads the 3.4 MB file and hydration work never
 * competes with first paint.
 */
export function HeroMedia({ videoSrc, posterSrc }: HeroMediaProps) {
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(min-width: 768px)').matches) return;

    const connection = (
      navigator as Navigator & { connection?: { saveData?: boolean } }
    ).connection;
    if (connection?.saveData) return;

    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => setShowVideo(true), {
        timeout: 2000,
      });
      return () => window.cancelIdleCallback(id);
    }

    const id = window.setTimeout(() => setShowVideo(true), 500);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <div className="absolute inset-0" aria-hidden="true">
      {/* eslint-disable-next-line @next/next/no-img-element -- static
          public asset; next/image adds no value for a full-bleed poster
          that is already preloaded at high priority */}
      <img
        src={posterSrc}
        alt=""
        fetchPriority="high"
        className="h-full w-full object-cover"
      />
      {showVideo ? (
        <video
          src={videoSrc}
          poster={posterSrc}
          preload="metadata"
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}
    </div>
  );
}
